import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Panel from '../../../../../components/outline/Panel';
import FormField from '../../../../../components/common/FormField';
import BasicLabel from '../../../../../components/common/BasicLabel';

import Rate from '../../../../../components/utils/Rate/Rate';
import FormTextArea from '../../../../../components/common/FormTextArea';
import NewImgUpload from '../../../../../components/NewImgUpload';
import LabelWithCount from '../../../../../components/common/LableWithCount';
import OpenOrderItemDetail from '../../../../../components/link/OpenOrderItemDetail';
import useFormValues from '../../common/hook/queries/useFormValues';
import Switch from '../../../../../components/input/Switch/Switch';
import ReviewCriteriaField from '../../components/ReviewCriteriaField';
import { ReviewStatus } from '../../common/types/ReviewDetailTypes';
import { setDateFormat } from '../../../../../lib/moment';
import {
  CS_GENERAL_REVIEW,
  CS_REPORTED_REVIEW,
  CS_REVIEW_CONTENT_LIMIT,
  MAXIMUM_NUMBER_OF_REVIEW_IMG,
  REVIEW_STATUS_TEXT,
} from '../../../../../constants/service/app/csReviewConstants';
import { ReviewCriteriaFindResponseDto } from '../../common/types/ReviewCriteriaTypes';
import { countCharactersExcludeNewlines } from '../../../../../lib/utils/countCharacters';
import OpenUserDetail from '../../../../../components/link/OpenUserDetail';

interface ICsReviewProps {
  criteriaInfo: ReviewCriteriaFindResponseDto;
}

const CsReviewEditForm = (props: ICsReviewProps): JSX.Element => {
  const { criteriaInfo } = props;
  const { control } = useFormContext();
  const { status, isReported, orderItemId, createDate, content, buyerName, buyerId, imgUrls } = useFormValues();
  const characterCount = countCharactersExcludeNewlines(content);

  return (
    <Panel className="cs-review-create-info">
      <div className="panel-header">
        <div className="title">후기 정보</div>
      </div>
      <section className="panel-body">
        <div className="review-info-wrap">
          <div className="review-info">
            <FormField label={<BasicLabel label={'구매자 이름'} isRequired={false} />}>
              <div className="info-wrap">{buyerName}</div>
            </FormField>
            <FormField label={<BasicLabel label={'구매자 ID'} isRequired={false} />}>
              <div className="info-wrap">
                <OpenUserDetail text={buyerId} userId={buyerId} />
              </div>
            </FormField>
            <FormField label={<BasicLabel label={'상품 주문서 ID'} isRequired={false} />}>
              <div className="info-wrap">
                <OpenOrderItemDetail text={orderItemId} orderItemId={orderItemId} />
              </div>
            </FormField>
            <FormField label={<BasicLabel label={'상태'} isRequired={false} />}>
              <div className="info-wrap">{REVIEW_STATUS_TEXT[status as ReviewStatus]}</div>
            </FormField>
            <FormField label={<BasicLabel label={'신고 여부'} isRequired={false} />}>
              <div className="info-wrap">{isReported ? CS_REPORTED_REVIEW : CS_GENERAL_REVIEW}</div>
            </FormField>
            <FormField label={<BasicLabel label={'작성일'} isRequired={false} />}>
              <div>{setDateFormat(createDate, 'LLL')}</div>
            </FormField>
            <FormField label={<BasicLabel label={'노출여부'} isRequired={false} />}>
              <Controller
                name="status"
                control={control}
                defaultValue={status as ReviewStatus}
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className="content-wrap">
                    <Switch
                      checked={value === ReviewStatus['SUBMIT']}
                      onChange={(checked: boolean) => {
                        if (!checked) {
                          onChange(ReviewStatus['DELETED_FROM_ADMIN']);
                        } else {
                          onChange(ReviewStatus['SUBMIT']);
                        }
                      }}
                    />
                    {<p className="error-message">{error ? error.message : null}</p>}
                  </div>
                )}
              />
            </FormField>
            <FormField label={<BasicLabel label={'평점'} isRequired={false} />}>
              <Controller
                name="rate"
                control={control}
                defaultValue={3}
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className="content-wrap">
                    <Rate allowClear={false} value={Number(value)} name="rate" onChange={onChange} />
                    {<p className="error-message">{error ? error.message : null}</p>}
                  </div>
                )}
              />
            </FormField>
            {criteriaInfo?.reviewCriteria && criteriaInfo?.reviewCriteria.length > 0 && (
              <FormField label={<BasicLabel label={'평가 항목'} isRequired={true} />}>
                <Controller
                  name="reviewCriteria"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <div className="content-wrap">
                      <ReviewCriteriaField list={criteriaInfo.reviewCriteria} value={value} onChange={onChange} />
                      {<p className="error-message">{error ? error.message : null}</p>}
                    </div>
                  )}
                />
              </FormField>
            )}
            <FormField
              label={
                <LabelWithCount
                  label={'구매평'}
                  isRequired={true}
                  baseNumber={CS_REVIEW_CONTENT_LIMIT}
                  count={characterCount}
                />
              }
            >
              <FormTextArea
                id="content"
                type="text"
                name="content"
                label="구매평"
                placeholder="구매평"
                editable={true}
                limit={CS_REVIEW_CONTENT_LIMIT}
              />
            </FormField>
            <FormField
              label={
                <LabelWithCount
                  label={'상품 후기 이미지'}
                  isRequired={false}
                  baseNumber={MAXIMUM_NUMBER_OF_REVIEW_IMG}
                  count={imgUrls?.length}
                />
              }
            >
              <Controller
                name="imgUrls"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="content-wrap">
                    <NewImgUpload
                      name="imgUrls"
                      modalTitle="상품 후기 이미지"
                      maxCnt={MAXIMUM_NUMBER_OF_REVIEW_IMG}
                      onChange={onChange}
                      value={value}
                    />
                  </div>
                )}
              />
            </FormField>
          </div>
        </div>
      </section>
    </Panel>
  );
};

export default CsReviewEditForm;
